import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Button } from './Button';
import { isValidElement } from 'react';

interface EmptyStateProps {
  icon?: LucideIcon;
  title: string;
  description?: string;
  action?: ReactNode | { label: string; onClick: () => void };
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

export function EmptyState({ icon: Icon, title, description, action, className = '', size = 'md' }: EmptyStateProps) {
  const isSm = size === 'sm';
  const isLg = size === 'lg';

  const renderAction = () => {
    if (!action) return null;

    if (isValidElement(action)) return action;

    if (typeof action === 'object' && 'label' in action && 'onClick' in action) {
      return <Button onClick={action.onClick} size={isSm ? 'sm' : 'md'} title={action.label} />;
    }

    return action as ReactNode;
  };

  return (
    <div
      className={`relative flex flex-col items-center justify-center text-center backdrop-blur-sm transition-all ${
        isSm ? 'p-6' : isLg ? 'p-16 md:p-24' : 'p-10 md:p-16'
      } ${className}`.trim()}
    >
      <div className={`relative ${isSm ? 'mb-4' : 'mb-6'}`}>
        <div
          className={`relative flex items-center justify-center rounded-3xl bg-lime-400 text-gray-400 ${
            isSm ? 'h-14 w-14' : 'h-20 w-20'
          }`}
        >
          {Icon ? (
            <Icon className={`${isSm ? 'h-7 w-7' : 'h-10 w-10'} text-lime-800`} strokeWidth={1.5} />
          ) : (
            <div className={`${isSm ? 'h-7 w-7' : 'h-10 w-10'} rounded-lg bg-gray-100 animate-pulse`} />
          )}
        </div>
      </div>

      <h3
        className={`max-w-sm font-black tracking-tight text-gray-900 ${isSm ? 'text-sm' : isLg ? 'text-2xl md:text-3xl' : 'text-xl md:text-2xl'}`}
      >
        {title}
      </h3>

      {description && (
        <p
          className={`mx-auto mt-2 max-w-sm font-semibold leading-relaxed text-gray-500 ${isSm ? 'text-[11px]' : isLg ? 'text-base md:text-lg' : 'text-sm md:text-base'}`}
        >
          {description}
        </p>
      )}

      {action && (
        <div className={`${isSm ? 'mt-6' : 'mt-10'} animate-in fade-in slide-in-from-bottom-4 duration-700`}>
          {renderAction()}
        </div>
      )}
    </div>
  );
}
